import { useState } from 'react'
import {
  ensureFolderPath,
  initiateUpload,
  completeUpload,
  type InitiateUploadResult,
} from '@/lib/api-client'
import { getAttachmentContentAsync } from '@/lib/office'
import type { AttachmentMeta } from '@/types'

export interface AttachmentUploadResult {
  attachmentId: string
  documentId: string
  name: string
}

export interface UploadProgress {
  attachmentId: string
  state: 'pending' | 'fetching' | 'hashing' | 'uploading' | 'finalizing' | 'done' | 'error'
  progress: number // 0..1
  error?: string
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function emailFolderPath(date: Date = new Date()): string {
  const yyyy = date.getUTCFullYear()
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
  return `/Correspondence/Emails/${yyyy}-${mm}`
}

async function putBytes(url: string, body: ArrayBuffer, contentType: string): Promise<string> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body,
  })
  if (!res.ok) throw new Error(`PUT failed: HTTP ${res.status}`)
  return (res.headers.get('etag') ?? res.headers.get('ETag') ?? '').replaceAll('"', '')
}

export function useAttachmentUpload() {
  const [progress, setProgress] = useState<UploadProgress[]>([])

  const update = (id: string, patch: Partial<UploadProgress>) => {
    setProgress((p) =>
      p.map((x) => (x.attachmentId === id ? { ...x, ...patch } : x))
    )
  }

  /**
   * Uploads each provided attachment into /Correspondence/Emails/YYYY-MM/
   * and returns the matter_documents IDs. The caller passes these IDs to
   * linkEmail so the bridge linkage stamps source_email_id + populates
   * matter_emails.matter_document_ids.
   */
  async function uploadAttachments(
    matterId: string,
    attachments: AttachmentMeta[]
  ): Promise<AttachmentUploadResult[]> {
    if (attachments.length === 0) return []

    setProgress(
      attachments.map((a) => ({ attachmentId: a.id, state: 'pending', progress: 0 }))
    )

    // Resolve target folder once (idempotent).
    const folderRes = await ensureFolderPath(matterId, emailFolderPath())
    if (folderRes.error || !folderRes.data) {
      throw new Error(folderRes.error ?? 'Failed to resolve attachment folder')
    }
    const folderId = folderRes.data.id

    const results: AttachmentUploadResult[] = []

    for (const att of attachments) {
      try {
        update(att.id, { state: 'fetching', progress: 0.05 })
        const buf = await getAttachmentContentAsync(att.id)
        if (!buf) throw new Error('Failed to read attachment content')

        update(att.id, { state: 'hashing', progress: 0.15 })
        const contentHash = await sha256Hex(buf)

        update(att.id, { state: 'uploading', progress: 0.25 })
        const initRes = await initiateUpload(matterId, {
          name: att.name,
          contentHash,
          size: buf.byteLength,
          mimeType: att.contentType || 'application/octet-stream',
          folderId,
        })
        if (initRes.error || !initRes.data) {
          throw new Error(initRes.error ?? 'Initiate upload failed')
        }
        const init: InitiateUploadResult = initRes.data

        let uploadId: string | undefined
        let partETags: { PartNumber: number; ETag: string }[] | undefined

        if (init.mode === 'dedup') {
          update(att.id, { progress: 0.85 })
        } else if (init.mode === 'simple') {
          await putBytes(init.putUrl, buf, att.contentType || 'application/octet-stream')
          update(att.id, { progress: 0.85 })
        } else {
          uploadId = init.uploadId
          partETags = []
          for (let i = 0; i < init.partCount; i++) {
            const start = i * init.partSize
            const end = Math.min(start + init.partSize, buf.byteLength)
            const slice = buf.slice(start, end)
            const etag = await putBytes(
              init.partUrls[i]!,
              slice,
              att.contentType || 'application/octet-stream'
            )
            partETags.push({ PartNumber: i + 1, ETag: etag })
            update(att.id, { progress: 0.25 + (end / buf.byteLength) * 0.6 })
          }
        }

        update(att.id, { state: 'finalizing', progress: 0.92 })
        const finalize = await completeUpload(matterId, {
          contentHash,
          name: att.name,
          size: buf.byteLength,
          mimeType: att.contentType || 'application/octet-stream',
          folderId,
          ...(uploadId ? { uploadId } : {}),
          ...(partETags ? { partETags } : {}),
        })
        if (finalize.error || !finalize.data) {
          throw new Error(finalize.error ?? 'Finalize failed')
        }

        update(att.id, { state: 'done', progress: 1 })
        results.push({
          attachmentId: att.id,
          documentId: finalize.data.id,
          name: finalize.data.name,
        })
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'upload failed'
        update(att.id, { state: 'error', progress: 0, error: msg })
        // Continue with the next attachment — don't fail the whole batch.
      }
    }

    return results
  }

  function reset() {
    setProgress([])
  }

  return { progress, uploadAttachments, reset }
}
